import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/site/Motion";
import { site, information } from "@/content/site";

export const Workflow = () => (
  <section className="bg-[var(--navy)] text-[var(--paper)] relative grain overflow-hidden" data-testid="workflow-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32">
      <div className="grid lg:grid-cols-12 gap-10 mb-16">
        <Reveal className="lg:col-span-6">
          <p className="eyebrow !text-[var(--gold)] mb-5">For authors</p>
          <h2 className="font-display text-5xl sm:text-6xl leading-[1.02]">Register once. <em>Then a five-step submission.</em></h2>
        </Reveal>
        <Reveal delay={0.1} className="lg:col-span-5 lg:col-start-8 self-end">
          <p className="text-lg text-[#C9D2CC] leading-relaxed">{information.authors.body}</p>
        </Reveal>
      </div>
      <ol className="grid sm:grid-cols-2 lg:grid-cols-5 border-t border-white/20">
        {site.workflow.map((s, i) => (
          <Reveal as="li" key={s} delay={i * 0.08} className="border-b lg:border-b-0 lg:border-r last:border-r-0 border-white/20 py-8 lg:px-6 first:lg:pl-0">
            <span className="font-mono-x text-sm text-[var(--gold)]">0{i + 1}</span>
            <p className="font-display text-3xl mt-6">{s}</p>
          </Reveal>
        ))}
      </ol>
      <Reveal className="mt-14 flex flex-wrap gap-4">
        <Link to="/register" className="inline-flex items-center h-14 px-7 rounded-full bg-[var(--paper)] text-[var(--navy)] hover:bg-[var(--gold)] transition-colors" data-testid="workflow-register-button">Register to submit</Link>
        <Link to="/index.php/ESGS/about/submissions" className="inline-flex items-center gap-2 h-14 px-7 rounded-full border border-white/50 hover:bg-white hover:text-[var(--navy)] transition-colors" data-testid="workflow-guidelines-button">Read the guidelines <ArrowUpRight className="w-4 h-4" /></Link>
      </Reveal>
    </div>
  </section>
);

const LogoLink = ({ item, testid }) => (
  <a href={item.href} target="_blank" rel="noopener noreferrer" className="group flex items-center justify-center h-24 bg-white border border-[var(--line)] px-6 hover:border-[var(--emerald)] transition-colors" data-testid={testid}>
    <img src={item.image} alt={item.name || item.label} loading="lazy" className="max-h-10 w-auto object-contain grayscale group-hover:grayscale-0 transition-[filter] duration-500" />
  </a>
);

export const Resources = () => (
  <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="resources-section">
    <div className="grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-4">
        <p className="eyebrow mb-5">Author resources</p>
        <h2 className="font-display text-5xl leading-[1.02]">Tools that travel <em>with your manuscript.</em></h2>
      </Reveal>
      <div className="lg:col-span-7 lg:col-start-6 space-y-10">
        {[["English template", [site.template]], ["Indexing sites", site.indexing], ["Recommended tools", site.tools]].map(([title, items], gi) => (
          <Reveal key={title} delay={gi * 0.08}>
            <h3 className="font-mono-x text-xs tracking-[0.2em] uppercase text-[var(--ink-muted)] mb-4">{title}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {items.map((it) => <LogoLink key={it.href} item={it} testid={`resource-${(it.name || "template").toLowerCase()}`} />)}
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const InfoTriptych = () => (
  <section className="border-t border-[var(--line)]" data-testid="information-section">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid md:grid-cols-3">
      {Object.entries(information).map(([k, v], i) => (
        <Reveal key={k} delay={i * 0.08} className="border-b md:border-b-0 md:border-r last:border-r-0 border-[var(--line)] py-14 md:px-8 first:md:pl-0">
          <Link to={`/index.php/ESGS/information/${k}`} className="group block" data-testid={`info-card-${k}`}>
            <p className="eyebrow mb-6">Information</p>
            <h3 className="font-display text-4xl mb-4 flex items-start justify-between gap-4">For {k[0].toUpperCase() + k.slice(1)} <ArrowUpRight className="w-6 h-6 shrink-0 mt-2 transition-transform duration-500 group-hover:rotate-45" /></h3>
            <p className="text-[var(--ink-muted)] leading-relaxed line-clamp-3">{v.body}</p>
          </Link>
        </Reveal>
      ))}
    </div>
  </section>
);
